import { useCallback } from "react"

import { useParams } from "react-router-dom"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { DEFAULT_SIGN_ICON } from "@l2r-front/l2r-map"
import { useIsRoadWriter, useRoleIsReadOnly } from "@l2r-front/l2r-networks"
import { PropTypes } from "@l2r-front/l2r-proptypes"
import {
    CatalogList,
    CatalogListError,
    CatalogListSkeleton,
} from "@l2r-front/l2r-ui"
import { useShiftKeyShortcut } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { API_MODULE_VERTICAL_SIGNING } from "../../../../common/constants/modules"
import { useVerticalSigningContext } from "../../contexts/VerticalSigningContext"
import { useVerticalSignType, useVerticalSignTypes } from "../../hooks/queries/verticalSigning/useVerticalSignTypes"

import { VerticalSignsCount } from "../VerticalSignsCount/VerticalSignsCount"

export function VerticalSignTypeList(props) {

    const {
        baseVerticalSignTypeCode,
        className,
        onChangeFilter,
        onCreate,
    } = props

    const { t } = useTranslation()
    const { data } = useVerticalSignType(baseVerticalSignTypeCode)
    const [state, dispatch] = useVerticalSigningContext()
    const { ascendantCodes, filters } = state
    const { road } = useParams()
    const readOnly = useRoleIsReadOnly(null, API_MODULE_VERTICAL_SIGNING)
    const isRoadWriter = useIsRoadWriter(road, API_MODULE_VERTICAL_SIGNING)
    const canCreate = road ? isRoadWriter : !readOnly

    useShiftKeyShortcut("n", onCreate, !!canCreate && !!onCreate)

    const {
        data: verticalSignTypes,
        isError: isErrorVerticalSignTypes,
        isLoading: isLoadingVerticalSignTypes,
        isFetching: isFetchingVerticalSignTypes,
    } = useVerticalSignTypes(baseVerticalSignTypeCode)

    const { setFilters } = dispatch

    const title = data?.name ?? t(I18N_NAMESPACE, "containers.verticalSignTypeList.defaultTitle")

    const applyFilter = useCallback((newFilter) => {
        if (filters.type === newFilter || ascendantCodes.indexOf(newFilter) >= 0) {
            const filterIndex = ascendantCodes.indexOf(newFilter)
            if (filterIndex !== ascendantCodes.length - 1) {
                setFilters({ type: ascendantCodes[filterIndex + 1] })
            } else {
                setFilters({ type: null })
            }
        } else {
            setFilters({ type: newFilter })
            onChangeFilter?.({ type: newFilter })
        }
    }, [filters, ascendantCodes, setFilters, onChangeFilter])

    if (isErrorVerticalSignTypes && !isFetchingVerticalSignTypes) {
        return <CatalogListError className={className} errorText={t(I18N_NAMESPACE, "components.verticalSignTypeList.error")} />
    }

    if (isLoadingVerticalSignTypes) {
        return <CatalogListSkeleton className={className} />
    }

    return <CatalogList
        onClick={applyFilter}
        baseItemCode={baseVerticalSignTypeCode}
        ascendantCodes={ascendantCodes}
        className={className}
        data={verticalSignTypes}
        defaultIcon={DEFAULT_SIGN_ICON}
        emptyListText={t(I18N_NAMESPACE, "components.verticalSignTypeList.emptyList")}
        filter={filters?.type}
        ItemsCountComponent={VerticalSignsCount}
        title={title}
        onCreate={canCreate ? onCreate : null}
    />
}

VerticalSignTypeList.propTypes = {
    baseVerticalSignTypeCode: PropTypes.string,
    className: PropTypes.string,
    onChangeFilter: PropTypes.func,
    onCreate: PropTypes.func,
}